import type { CSSProperties, LegacyRef, ReactNode } from 'react'
import React from 'react'
import classNames from 'classnames'
import ReactModal from 'react-modal'

import { PopoverParent } from '../Popover/PopoverParent'
import {
  MODAL_DEFAULT_CLASS,
  MODAL_DEFAULT_STYLE,
  MODAL_FULLSCREEN_STYLE,
} from './style'

export type ModalProps = {
  children?: ReactNode
  align?: 'center' | 'start'
  modalRef?: LegacyRef<ReactModal>
  wide?: boolean
  auto?: boolean
  size?: string | number
  maxWidthEnabled?: boolean
  overlayStyle?: CSSProperties
  isOpen?: boolean
  onRequestClose?: () => void
  fullScreenModal?: boolean
  className?: string
  shouldCloseOnEsc?: boolean
  portalClassName?: string
  style?: CSSProperties
  enableFullWidthEditor?: boolean
  isFadingOut?: boolean
  contentRef?: (el: HTMLDivElement) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void

  overlayOpacity?: 'opaque' | 'transparent'
  customOverlayElement?: (
    props: React.ComponentPropsWithRef<'div'>,
    children: React.ReactElement
  ) => React.ReactElement

  shouldCloseOnOverlayClick?: boolean

  bottomElement?: ReactNode
}

export function Modal({
  children,
  modalRef,
  wide = false,
  align = 'center',
  auto = false,
  size,
  maxWidthEnabled = true,
  overlayStyle,
  isOpen = false,
  onRequestClose,
  fullScreenModal,
  className = MODAL_DEFAULT_CLASS,
  style: contentStyleOverride,
  shouldCloseOnEsc = false,
  portalClassName,
  enableFullWidthEditor = false,
  shouldCloseOnOverlayClick = true,
  isFadingOut,
  contentRef,
  onKeyDown,

  overlayOpacity = 'transparent',

  customOverlayElement,

  bottomElement,
}: ModalProps) {
  let width = auto ? 'auto' : 665
  if (wide) {
    width = 800
  }
  if (size) {
    width = size
  }
  const { top, left, transform, margin, ...contentStyle } =
    MODAL_DEFAULT_STYLE.content as CSSProperties

  const style = fullScreenModal
    ? MODAL_FULLSCREEN_STYLE
    : {
        content: {
          ...contentStyle,
          width,
          ...(maxWidthEnabled
            ? { maxWidth: 'min(1200px, 90%)' }
            : { maxWidth: ' 95vw' }),
          top,
          left,
          transform,
          margin,
          ...contentStyleOverride,
        },
        overlay: {
          ...MODAL_DEFAULT_STYLE.overlay,
          ...overlayStyle,
          ...(align === 'center'
            ? { alignItems: 'center ' }
            : {
                alignItems: 'flex-start',
                paddingTop: '5vh',
                paddingBottom: '5vh',
              }),
        },
      }

  const onCloseWithFade = React.useCallback(() => {
    if (onRequestClose) {
      setTimeout(() => {
        onRequestClose()
      }, 100)
    }
  }, [onRequestClose])

  const hasCustomOverlay = !!bottomElement || !!customOverlayElement

  return (
    <ReactModal
      data={{ 'full-width-editor-in-modal': enableFullWidthEditor }}
      style={style}
      isOpen={isOpen}
      ref={modalRef}
      // So that we can control it from the state (see useSetPreventCloseModalOnEscape)
      shouldCloseOnEsc={shouldCloseOnEsc}
      onRequestClose={onCloseWithFade}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick && !fullScreenModal}
      shouldReturnFocusAfterClose={false}
      portalClassName={portalClassName}
      overlayClassName={classNames(
        'modal--with-fade-in-overlay',

        {
          'modal--fading-out': isFadingOut,

          // overlay
          'blurred-overlay': overlayOpacity === 'transparent',
          'blurred-overlay-opaque': overlayOpacity === 'opaque',
        }
      )}
      className={classNames('modal--with-fade-in', {
        'radius-large': !fullScreenModal && !hasCustomOverlay,
        'modal--fading-out': isFadingOut,
      })}
      overlayElement={
        !!hasCustomOverlay && customOverlayElement
          ? (props, children) => customOverlayElement(props, children)
          : undefined
      }
      contentRef={contentRef}
    >
      <PopoverParent>
        {(ref) => (
          <div
            ref={ref}
            className={classNames(
              fullScreenModal ? 'width-full' : 'radius-xlarger overflow-hidden',
              className
            )}
            onKeyDown={onKeyDown}
          >
            {children}
          </div>
        )}
      </PopoverParent>
      {bottomElement ? bottomElement : null}
    </ReactModal>
  )
}
